import { SUPPORTED_LANGUAGES } from '../../../employees/utils/employee-languages';
import { combineWords } from '../../../util/formatters';
import { LANGUAGES, LanguageCode } from '../../../util/languages';

const conditionallyPluralizeRecipient = (count: number) =>
  `${count > 1 ? `${count} recipients` : 'recipient'}`;

const conjugatePrefer = (count: number) => `prefer${count > 1 ? '' : 's'}`;

const bannerMessageEnding = 'You can translate from an English message below.';

const getMonolingualLanguageBannerMessage = (
  language: string,
  languageCounts: Record<LanguageCode, number>,
) => {
  const recipientCount = languageCounts[language];

  return `The selected ${conditionallyPluralizeRecipient(recipientCount)} ${conjugatePrefer(
    recipientCount,
  )} to communicate in ${LANGUAGES[language]}. ${bannerMessageEnding}`;
};

const getMultipleLanguagesBannerMessage = (
  language: string,
  languageCounts: Record<LanguageCode, number>,
  messagesByLanguage: Record<LanguageCode, string>,
) => {
  const recipientLanguages: Array<string> = [];
  const otherSupportedLanguagesWithMessage: Array<string> = [];
  Object.keys(languageCounts).forEach((lang) => {
    if (SUPPORTED_LANGUAGES.includes(lang)) {
      if (lang !== language && lang in messagesByLanguage) {
        otherSupportedLanguagesWithMessage.push(lang);
      } else {
        recipientLanguages.push(lang);
      }
    } else {
      recipientLanguages.push(lang);
    }
  });

  const recipientCount = recipientLanguages.reduce(
    (sum, recipientLanguage) => sum + languageCounts[recipientLanguage],
    0,
  );
  const otherSupportedLanguagesRecipientCount = otherSupportedLanguagesWithMessage.reduce(
    (sum, otherSupportedLanguage) => sum + languageCounts[otherSupportedLanguage],
    0,
  );
  const recipientLanguagesNames = recipientLanguages.map(
    (recipientLanguage) => LANGUAGES[recipientLanguage],
  );

  if (otherSupportedLanguagesWithMessage.length === 0) {
    return `This message will be sent to all${
      recipientCount > 1 ? ` ${recipientCount}` : ''
    } recipients, regardless of language preference.`;
  }

  const message = [
    `Among the selected recipients, ${recipientCount} ${conjugatePrefer(
      recipientCount,
    )} to communicate in ${combineWords(recipientLanguagesNames, 'or')}`,
  ];

  if (otherSupportedLanguagesWithMessage.length) {
    message.push(
      `${otherSupportedLanguagesRecipientCount} ${
        otherSupportedLanguagesWithMessage.length > 1
          ? ''
          : `${conjugatePrefer(otherSupportedLanguagesRecipientCount)} ${
              LANGUAGES[otherSupportedLanguagesWithMessage[0]]
            }`
      }. Please add a message for each group. ${bannerMessageEnding}`,
    );
  }

  return message.join(' and ');
};

export const getLanguagePreferenceBannerMessages = (
  languageCounts: Record<LanguageCode, number>,
  messagesByLanguage: Record<LanguageCode, string>,
  defaultLanguage = 'en',
) => {
  const recipientLanguages = Object.keys(languageCounts);
  if (recipientLanguages.length === 1) {
    const language = recipientLanguages[0];
    if (language === defaultLanguage) {
      return '';
    }

    return getMonolingualLanguageBannerMessage(language, languageCounts);
  }

  return getMultipleLanguagesBannerMessage(defaultLanguage, languageCounts, messagesByLanguage);
};
